import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigationType, useSearchParams } from "react-router-dom";
import useSWR from "swr";
import { X, ListMusic } from "lucide-react";
import { fetchCategories, fetchVideos, listCollections } from "@/lib/api";
import FilterBar from "@/components/FilterBar";
import VideoCard from "@/components/VideoCard";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import usePageMeta from "@/hooks/use-page-meta";

const PAGE_SIZE = 24;

export default function Archive() {
  const [searchParams, setSearchParams] = useSearchParams();

  const [query, setQuery] = useState(searchParams.get("q") || "");
  const [debouncedQuery, setDebouncedQuery] = useState(query);
  const [category, setCategory] = useState(searchParams.get("category") || "All");
  const [sort, setSort] = useState(searchParams.get("sort") || "newest");
  const [playlist, setPlaylist] = useState(searchParams.get("playlist") || "");
  const [page, setPage] = useState(0);

  const location = useLocation();
  const navType = useNavigationType();

  // Sync filters when a link (e.g. footer) navigates to /archive with new params
  useEffect(() => {
    if (navType === "REPLACE") return;
    const q = searchParams.get("q") || "";
    setQuery(q);
    setDebouncedQuery(q);
    setCategory(searchParams.get("category") || "All");
    setSort(searchParams.get("sort") || "newest");
    setPlaylist(searchParams.get("playlist") || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query), 300);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => {
    setPage(0);
  }, [debouncedQuery, category, sort, playlist]);

  useEffect(() => {
    const next = new URLSearchParams();
    if (debouncedQuery) next.set("q", debouncedQuery);
    if (category && category !== "All") next.set("category", category);
    if (sort && sort !== "newest") next.set("sort", sort);
    if (playlist) next.set("playlist", playlist);
    setSearchParams(next, { replace: true });
  }, [debouncedQuery, category, sort, playlist, setSearchParams]);

  // If a playlist is chosen and sort hasn't been explicitly set, default to playlist order
  useEffect(() => {
    if (playlist && !searchParams.get("sort")) setSort("playlist");
    if (!playlist && sort === "playlist") setSort("newest");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playlist]);

  const { data: catsData } = useSWR("/videos/categories", fetchCategories);
  const { data: colsData } = useSWR("/collections", () => listCollections());

  const categories = useMemo(() => {
    const list = [{ name: "All", count: catsData?.total || 0 }];
    if (catsData?.categories) list.push(...catsData.categories);
    return list;
  }, [catsData]);

  const playlists = useMemo(() => {
    const all = colsData?.items || [];
    // Exclude Pet Rescue Saga playlists — those live on the dedicated /pet-rescue-saga page.
    const filtered = all.filter(
      (c) => !/^Pet Rescue Saga/i.test(c.title || "")
    );
    // Prioritize YouTube-imported playlists first
    return [...filtered].sort((a, b) => {
      const ay = a.source === "youtube" ? 0 : 1;
      const by = b.source === "youtube" ? 0 : 1;
      return ay - by || b.count - a.count;
    });
  }, [colsData]);

  const activePlaylist = playlist
    ? playlists.find((p) => p.slug === playlist)
    : null;

  usePageMeta({
    title: activePlaylist
      ? `${activePlaylist.title} — ${activePlaylist.count} videos`
      : debouncedQuery
      ? `Search: "${debouncedQuery}"`
      : category && category !== "All"
      ? `${category} — Archive`
      : "Full Video Archive — Search Every tobiasdeamon Upload",
    description: activePlaylist
      ? `Every video in the ${activePlaylist.title} playlist, in Lukas's exact order — ${activePlaylist.count} entries.`
      : `Browse ${catsData?.total || ""} videos in the tobiasdeamon Vault. Search by title or category, sort newest-first, most viewed, or by duration.`,
  });

  const key = ["/videos", { q: debouncedQuery, category, sort, playlist, page }];
  const { data, isLoading } = useSWR(key, () =>
    fetchVideos({
      q: debouncedQuery,
      category,
      sort,
      playlist: playlist || undefined,
      limit: PAGE_SIZE,
      skip: page * PAGE_SIZE,
    })
  );

  const items = data?.items || [];
  const total = data?.total || 0;
  const hasMore = (page + 1) * PAGE_SIZE < total;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
      <div className="mb-8 sm:mb-10">
        <div className="mono-label">The Archive</div>
        <h1 className="font-serif-editorial text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#f4f1ea] mt-3">
          {activePlaylist ? activePlaylist.title : "Browse every catalogued upload."}
        </h1>
        {!activePlaylist && (
          <p
            className="mt-4 sm:mt-5 max-w-2xl text-base sm:text-lg text-white/60 leading-relaxed"
            data-testid="archive-lede"
          >
            24,000+ videos, every one indexed, tagged and searchable. Filter by category,
            sort by date, or group by playlist.
          </p>
        )}
        {activePlaylist && (
          <p className="mt-3 text-white/60 max-w-2xl">
            Viewing videos in the exact order of your playlist. Clear the filter to browse
            the full archive again.
          </p>
        )}
      </div>

      {/* Playlist selector row */}
      <div className="mb-6 flex flex-wrap items-center gap-2 sm:gap-3" data-testid="playlist-filter-row">
        <div className="inline-flex items-center gap-2 mono-label text-white/40">
          <ListMusic className="w-3.5 h-3.5" /> Group by playlist
        </div>
        <Select
          value={playlist || "__none__"}
          onValueChange={(val) => setPlaylist(val === "__none__" ? "" : val)}
        >
          <SelectTrigger
            data-testid="playlist-select"
            className="w-full sm:w-[300px] max-w-full h-11 rounded-full bg-[#12141d] border-white/10 font-mono-tech text-[11px] uppercase tracking-widest text-white/70"
          >
            <SelectValue placeholder="Choose a playlist…" />
          </SelectTrigger>
          <SelectContent className="bg-[#12141d] border-white/10 text-[#f4f1ea] max-h-96">
            <SelectItem value="__none__" className="font-mono-tech text-xs uppercase tracking-widest">
              Off — browse full archive
            </SelectItem>
            {playlists.map((p) => (
              <SelectItem
                key={p.slug}
                value={p.slug}
                data-testid={`playlist-option-${p.slug}`}
                className="text-sm"
              >
                <span className="inline-flex items-center gap-2">
                  {p.source === "youtube" && (
                    <span className="text-[#d95d39] text-[10px] font-mono-tech">YT</span>
                  )}
                  {p.title}
                  <span className="text-white/40 text-[11px] ml-2">
                    · {new Intl.NumberFormat().format(p.count)}
                  </span>
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {playlist && (
          <button
            onClick={() => setPlaylist("")}
            data-testid="playlist-clear-btn"
            className="inline-flex items-center gap-1 mono-label text-white/50 hover:text-[#d95d39] transition-colors"
          >
            <X className="w-3.5 h-3.5" /> Clear
          </button>
        )}
      </div>

      <FilterBar
        query={query}
        onQueryChange={setQuery}
        categories={categories}
        activeCategory={category}
        onCategoryChange={setCategory}
        sort={sort}
        onSortChange={setSort}
        totalCount={total}
        playlistMode={Boolean(playlist)}
      />

      <div className="mt-10">
        {isLoading && items.length === 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="rounded-xl border border-white/10 bg-[#12141d] overflow-hidden">
                <Skeleton className="aspect-video w-full bg-white/5" />
                <div className="p-5 space-y-3">
                  <Skeleton className="h-4 w-4/5 bg-white/5" />
                  <Skeleton className="h-3 w-2/5 bg-white/5" />
                </div>
              </div>
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="py-24 text-center">
            <div className="mono-label text-white/40">No results</div>
            <p className="font-serif-editorial text-3xl text-[#f4f1ea] mt-4">Nothing matched your query.</p>
            <p className="text-white/50 mt-2">Try clearing filters or searching a different term.</p>
          </div>
        ) : (
          <>
            <div
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
              data-testid="video-grid-container"
            >
              {items.map((v, i) => (
                <div key={v.id} className="relative">
                  {playlist && sort === "playlist" && (
                    <span className="absolute -top-2 -left-2 z-10 w-8 h-8 rounded-full bg-[#d95d39] border border-black/30 text-white text-xs font-mono-tech flex items-center justify-center shadow-lg">
                      {page * PAGE_SIZE + i + 1}
                    </span>
                  )}
                  <VideoCard video={v} />
                </div>
              ))}
            </div>

            {hasMore && (
              <div className="flex justify-center mt-14">
                <Button
                  data-testid="load-more-btn"
                  onClick={() => setPage((p) => p + 1)}
                  className="px-8 py-6 rounded-full bg-transparent border border-white/20 text-[#f4f1ea] font-mono-tech text-xs uppercase tracking-[0.22em] hover:bg-[#d95d39] hover:border-[#d95d39] hover:text-white"
                >
                  Load more · {new Intl.NumberFormat().format(total - (page + 1) * PAGE_SIZE)} left
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
